const stateByWindow = new WeakMap();
const fallbackWindowKey = {};

const getWindowKey = (windowRef) =>
  windowRef && typeof windowRef === "object"
    ? windowRef
    : typeof window !== "undefined"
    ? window
    : fallbackWindowKey;

const getState = (windowRef) => {
  const key = getWindowKey(windowRef);
  let state = stateByWindow.get(key);
  if (!state) {
    state = {
      stream: null,
      pending: null,
      leases: new Set(),
    };
    stateByWindow.set(key, state);
  }
  return state;
};

const isLiveStream = (stream) => {
  const tracks = stream?.getAudioTracks?.() || stream?.getTracks?.() || [];
  return tracks.some((track) => track && track.readyState !== "ended");
};

export const getPersistentMicrophoneConstraints = (
  windowRef = typeof window !== "undefined" ? window : null
) => {
  return {
    audio: {
      channelCount: 1,
      echoCancellation: true,
      noiseSuppression: true,
      autoGainControl: true,
    },
    video: false,
  };
};

const stopStream = (stream) => {
  try {
    stream?.getTracks?.().forEach((track) => track.stop());
  } catch {
    // The browser owns device cleanup; ignore best-effort stop failures.
  }
};

export const acquirePersistentMicrophone = async ({
  windowRef = typeof window !== "undefined" ? window : null,
} = {}) => {
  const mediaDevices = windowRef?.navigator?.mediaDevices;
  if (!mediaDevices || typeof mediaDevices.getUserMedia !== "function") {
    throw new Error("Microphone media devices are not available.");
  }

  const state = getState(windowRef);
  if (state.stream && !isLiveStream(state.stream)) {
    state.stream = null;
  }

  if (!state.stream) {
    if (!state.pending) {
      state.pending = mediaDevices
        .getUserMedia(getPersistentMicrophoneConstraints(windowRef))
        .then((stream) => {
          state.stream = stream;
          return stream;
        })
        .finally(() => {
          state.pending = null;
        });
    }
    await state.pending;
  }

  const token = Symbol("xima-persistent-microphone");
  state.leases.add(token);

  let released = false;
  return {
    stream: state.stream,
    release() {
      if (released) return;
      released = true;
      state.leases.delete(token);
      if (state.leases.size === 0) {
        stopStream(state.stream);
        state.stream = null;
      }
    },
  };
};

export const getCurrentPersistentMicrophoneStream = (
  windowRef = typeof window !== "undefined" ? window : null
) => {
  const state = getState(windowRef);
  return state.stream && isLiveStream(state.stream) ? state.stream : null;
};
